import { motion } from "framer-motion";

type LoaderProps = {
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
};

export default function Loader({ size = "md", className = "" }: LoaderProps) {
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6", 
    lg: "w-8 h-8"
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        className={`${sizeClasses[size]} relative`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1.2,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        {/* Outer ring */}
        <div className="absolute inset-0 rounded-full border-2 border-accent-cyan/20"></div>
        
        {/* Animated arc */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-transparent border-t-accent-cyan border-r-accent-cyan/60"
          animate={{ rotate: 360 }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Inner pulsing dot */}
        <motion.div
          className="absolute top-1/2 left-1/2 w-1 h-1 bg-accent-cyan rounded-full transform -translate-x-1/2 -translate-y-1/2"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
    </div>
  );
}

// Alternative loader with scooter theme
export function ScooterLoader({ size = "md", className = "" }: LoaderProps) {
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8"
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        className={`${sizeClasses[size]} relative`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        {/* Wheel 1 */}
        <motion.div
          className="absolute top-0 left-0 w-2 h-2 bg-accent-cyan rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0
          }}
        />
        
        {/* Wheel 2 */}
        <motion.div
          className="absolute bottom-0 right-0 w-2 h-2 bg-accent-cyan rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.3
          }}
        />
        
        {/* Connecting line */}
        <motion.div
          className="absolute top-1/2 left-1/2 w-3 h-0.5 bg-accent-cyan/40 transform -translate-x-1/2 -translate-y-1/2"
          animate={{
            scaleX: [0.5, 1, 0.5],
            opacity: [0.4, 0.8, 0.4]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
    </div>
  );
}

// Minimalist dots loader
export function DotsLoader({ size = "md", className = "" }: LoaderProps) {
  const sizeClasses = {
    xs: "w-0.5 h-0.5",
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2"
  };

  return (
    <div className={`flex items-center justify-center space-x-1 ${className}`}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className={`${sizeClasses[size]} bg-accent-cyan rounded-full`}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.4, 1, 0.4]
          }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.2
          }}
        />
      ))}
    </div>
  );
}
